import { Checkbox, Flex, Text } from "@chakra-ui/react"
import { useRouter } from "next/router"
import { Dispatch, SetStateAction } from "react"
import { useBrandedTranslation } from "../../common/hooks/useBrandedTranslation"
import { AvailableZipOptionsType } from "../../common/types/product"
import { hasCadDrawings, isCadDrawingOption } from "../../common/utils/article-table"
import CheckboxIcon from "./CheckboxIcon"
import { CCheckmark, CMinus } from "./Icons"

type Props = {
  renderedOptions: Partial<AvailableZipOptionsType>
  setRenderedOptions: Dispatch<SetStateAction<Partial<AvailableZipOptionsType>>>
}

/* Use this to dynamically construct label keys for options */
const OPTION_LABEL_PREFIX = "pdp-downloadData-"

/* Virtual option name used for group checkbox and download options string construction */
const CAD_DRAWINGS_OPTION = "cadDrawings"

/* Virtual option name used for group checkbox and download options string construction */
const TENDER_TEXT_OPTION = "tendertext"

/* A reusable form providing chekboxes for zip file download options */
const ZipOptionsForm = ({ renderedOptions, setRenderedOptions }: Props) => {
  const { t } = useBrandedTranslation()
  const { locale } = useRouter()

  /* Get only the cad files options in an array */
  const cadFilesOptions = Object.entries(renderedOptions).filter((opt) => isCadDrawingOption(opt[0]))
  /* Get product specific files options in an array */
  const productTypesOptions = Object.entries(renderedOptions).filter((opt) => !isCadDrawingOption(opt[0]))
  /* Define if all or some cad files types are checked for styling purposes */
  const allCadFilesSelected = cadFilesOptions.filter((opt) => opt[0] !== CAD_DRAWINGS_OPTION).every((opt) => opt[1])
  const isCadIndeterminate =
    cadFilesOptions.filter((opt) => opt[0] !== CAD_DRAWINGS_OPTION).some((opt) => opt[1]) && !allCadFilesSelected

  /* Determine if we have one column with checkboxes in the modal or two to adjust size accordingly */
  const isDoubleColumns = !!cadFilesOptions?.length && !!productTypesOptions?.length

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e.target.name
    const value = e.target.checked

    const chkbox = {}
    chkbox[name] = value

    const updatedOptions = { ...renderedOptions, ...chkbox }

    const newRenderedOptions =
      renderedOptions[CAD_DRAWINGS_OPTION] !== undefined
        ? { ...updatedOptions, cadDrawings: hasCadDrawings(updatedOptions) }
        : updatedOptions

    setRenderedOptions(newRenderedOptions)
  }

  /* Use this function to control the group button for cad files */
  const handleChangeAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.checked

    const newCadOptions = cadFilesOptions.reduce((nco, option) => {
      nco[option[0]] = value
      return nco
    }, {})

    setRenderedOptions((prevRenderedOptions) => {
      return { ...prevRenderedOptions, ...newCadOptions }
    })
  }

  /* Util function to get the label key for CAD options based on attribute name */
  const getCadLabelKey = (optionKey: string) => {
    let suffix = optionKey.replace("_", "")
    suffix = suffix.slice(0, 1).toUpperCase() + suffix.slice(1)

    // Handle cad drawings label key
    if (suffix.includes("Drawings")) {
      suffix = suffix.replace("Drawings", "Data")
    }

    // Handle misspelled label keys for DXF files
    if (suffix.includes("Dxf")) {
      suffix = suffix.replace("Dxf", "Dfx")
    }

    return `${OPTION_LABEL_PREFIX}${suffix}`
  }

  /* Util function to get the label key for product specific options based on attribute name */
  const getProductLabelKey = (optionKey: string) => {
    const suffix = optionKey.includes("csv") ? optionKey.toUpperCase() : optionKey

    return `${OPTION_LABEL_PREFIX}${suffix}`
  }

  return (
    <Flex maxW="150" flexDirection={{ base: "column", xs: "row" }} gap={{ base: "3", xs: "0" }}>
      {/* CAD files cection, it has a special group button */}
      {!!cadFilesOptions?.length && (
        <Flex flexDirection="column" w={isDoubleColumns ? "50%" : "100%"} p="2" gap={{ base: "7", xs: "4" }}>
          {cadFilesOptions.map((option, index) => {
            const isFirst = index === 0
            const isChecked = isFirst ? allCadFilesSelected : option[1]
            const stateIcon =
              isFirst && isCadIndeterminate ? (
                <CheckboxIcon as={CMinus} />
              ) : isChecked ? (
                <CheckboxIcon as={CCheckmark} />
              ) : (
                <></>
              )

            return (
              <Checkbox
                isIndeterminate={isFirst && isCadIndeterminate}
                isChecked={isChecked}
                onChange={isFirst ? handleChangeAll : handleChange}
                name={option[0]}
                icon={stateIcon}
                key={option[0]}
                ml={isFirst ? "0" : "9"}
              >
                <Text fontSize="sm" fontWeight="normal" color="gray.900">
                  {t(getCadLabelKey(option[0]))}
                </Text>
              </Checkbox>
            )
          })}
        </Flex>
      )}
      {/* Product specific file types section, only simple checkboxes */}
      {!!productTypesOptions?.length && (
        <Flex flexDirection="column" w={isDoubleColumns ? "50%" : "100%"} p="2" gap={{ base: "7", xs: "4" }}>
          {productTypesOptions.map((option) => {
            /* Show the tender text option only for the German locale */
            if (option[0] === TENDER_TEXT_OPTION && locale !== "de-DE") return null

            return (
              <Checkbox
                isChecked={option[1]}
                onChange={handleChange}
                name={option[0]}
                icon={option[1] ? <CheckboxIcon as={CCheckmark} /> : <></>}
                key={option[0]}
              >
                <Text fontSize="sm" fontWeight="normal" color="gray.900">
                  {t(getProductLabelKey(option[0]))}
                </Text>
              </Checkbox>
            )
          })}
        </Flex>
      )}
    </Flex>
  )
}

export default ZipOptionsForm
